import { Component, HostListener, inject, OnInit, OnDestroy, signal, computed, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';
import { AccountLayoutComponent, AccountMenuItem, AccountAppearanceService, Appearance, AdminPageShellComponent } from '@dental-ui/account-layout';
import { AuthStateService, AuthApiService } from '@dental-ui/auth';
import { UserDto } from '@dental-ui/auth';
import { UserSettingsDto } from '@dental-ui/account/models';
import { ToastService, ProfileAvatarDialogComponent } from '@dental-ui/ui';
import { Subscription } from 'rxjs';
import {
  I18N_SETTINGS_MOUSE_CONTROLS,
  I18N_SETTINGS_MOUSE_CONTROLS_3SHAPE,
  I18N_SETTINGS_MOUSE_CONTROLS_DEFAULT,
  I18N_SETTINGS_MOUSE_CONTROLS_EXOCAD,
  MOUSE_CONTROL_3SHAPE,
  MOUSE_CONTROL_DEFAULT,
  MOUSE_CONTROL_EXOCAD,
  normalizeMouseControl,
  type MouseControlValue,
} from '@dental-ui/shared/consts';
import { DropdownOption } from '../models';
import { UserApiService, ChangePasswordRequest } from '@dental-ui/user-domain';
import { calculateInitials } from '@dental-ui/util';
import {
  LucideCamera,
  LucideEye,
  LucideEyeOff,
  LucideChevronDown,
  LucideSearch,
} from '@lucide/angular';

type SettingsTab = 'profile' | 'security' | 'preferences';

@Component({
  selector: 'lib-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    AccountLayoutComponent,
    AdminPageShellComponent,
    ProfileAvatarDialogComponent,
    LucideCamera,
    LucideEye,
    LucideEyeOff,
    LucideChevronDown,
    LucideSearch,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit, OnDestroy {
  @Input() hideLayout = false;
  readonly KEY_MOUSE_CONTROLS = I18N_SETTINGS_MOUSE_CONTROLS;
  private readonly translateService = inject(TranslateService);
  private readonly authStateService = inject(AuthStateService);
  private readonly authApiService = inject(AuthApiService);
  private readonly toastService = inject(ToastService);
  private readonly userApiService = inject(UserApiService);
  readonly appearanceService = inject(AccountAppearanceService);
  private languageChangeSubscription?: Subscription;

  // UserSettings state
  private userSettings: UserSettingsDto | null = null;

  /** Last user data from GET User/GetById; used as base when saving via PUT */
  private loadedUser: UserDto | null = null;

  readonly activeTab = signal<SettingsTab>('profile');
  readonly settingsTabs: { id: SettingsTab; labelKey: string }[] = [
    { id: 'profile', labelKey: 'SETTINGS.PROFILE' },
    { id: 'security', labelKey: 'SETTINGS.SECURITY' },
    { id: 'preferences', labelKey: 'SETTINGS.PREFERENCES' },
  ];

  accountMenuItems: AccountMenuItem[] = [
    { label: 'ACCOUNT_LAYOUT.MENU_ACCOUNT', route: '/account', icon: 'person' },
    { label: 'ACCOUNT_LAYOUT.MENU_SUBSCRIPTIONS', route: '/account/subscriptions', icon: 'description' },
    { label: 'ACCOUNT_LAYOUT.MENU_ORGANIZATION', route: '/account/organization', icon: 'business' },
    { label: 'ACCOUNT_LAYOUT.MENU_USERS', route: '/account/team', icon: 'people' },
    { label: 'ACCOUNT_LAYOUT.MENU_SERVICE_LIST', route: '/account/services', icon: 'medical_services' },
    { label: 'ACCOUNT_LAYOUT.MENU_PRICE_LIST', route: '/account/prices', icon: 'description' },
    { label: 'ACCOUNT_LAYOUT.MENU_PARTNERS', route: '/account/partners', icon: 'person_add' },
    { label: 'ACCOUNT_LAYOUT.MENU_FORMS', route: '/account/forms', icon: 'article' },
    { label: 'ACCOUNT_LAYOUT.MENU_COMMUNICATION', route: '/account/communication', icon: 'chat_bubble_outline' },
  ];

  // Profile form fields
  readonly firstName = signal('');
  readonly lastName = signal('');
  readonly email = signal('');
  readonly firstNameTouched = signal(false);
  readonly lastNameTouched = signal(false);
  readonly isProfileSaving = signal(false);
  readonly isAvatarModalOpen = signal(false);
  readonly roleDescription = signal<string | null>(null);
  readonly membershipDate = signal<Date | string | null>(null);

  // Prefer session URL as-is (cache-bust breaks signed avatar URLs).
  readonly userAvatarUrl = computed(() => {
    const url =
      (this.authStateService.user()?.userAvatarUrl || '').trim() ||
      (this.loadedAvatarUrl() || '').trim();
    return url || null;
  });

  private readonly loadedAvatarUrl = signal<string | null>(null);

  readonly userInitials = computed(() => {
    const firstName = this.firstName();
    const lastName = this.lastName();
    const lang = this.translateService.getCurrentLanguage() || 'tr';
    return calculateInitials(firstName, lastName, lang);
  });

  readonly canSaveProfile = computed(
    () => this.firstName().trim().length > 0 && this.lastName().trim().length > 0
  );

  readonly displayName = computed(() => {
    const first = this.firstName().trim();
    const last = this.lastName().trim();
    return `${first} ${last}`.trim();
  });

  readonly roleLabelKey = computed(() => {
    const role = (this.roleDescription() || this.authStateService.user()?.role || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
    return role ? `ACCOUNT_INFO.ROLE_${role}` : 'ACCOUNT_LAYOUT.PROFILE_HINT';
  });

  readonly memberSinceText = computed(() => {
    const raw = this.membershipDate();
    if (!raw) {
      return '';
    }
    const date = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const lang = this.translateService.getCurrentLanguage() || 'en';
    return date.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
  });

  setActiveTab(tab: SettingsTab): void {
    this.activeTab.set(tab);
  }

  // Password form fields
  readonly currentPassword = signal('');
  readonly newPassword = signal('');
  readonly confirmPassword = signal('');
  readonly showCurrentPassword = signal(false);
  readonly showNewPassword = signal(false);
  readonly showConfirmPassword = signal(false);
  readonly isPasswordChanging = signal(false);

  // Password requirements signals
  readonly passwordRequirements = {
    minLength: signal(false),
    hasUppercase: signal(false),
    hasLowercase: signal(false),
    hasNumber: signal(false),
    hasSpecialChar: signal(false),
  };

  /**
   * Check password requirements and update signals
   */
  readonly checkPasswordRequirements = (password: string) => {
    this.passwordRequirements.minLength.set(password.length >= 8);
    this.passwordRequirements.hasUppercase.set(/[A-Z]/.test(password));
    this.passwordRequirements.hasLowercase.set(/[a-z]/.test(password));
    this.passwordRequirements.hasNumber.set(/[0-9]/.test(password));
    this.passwordRequirements.hasSpecialChar.set(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password));
  };

  /**
   * Check if all password requirements are met
   */
  readonly arePasswordRequirementsMet = computed(() => {
    return (
      this.passwordRequirements.minLength() &&
      this.passwordRequirements.hasUppercase() &&
      this.passwordRequirements.hasLowercase() &&
      this.passwordRequirements.hasNumber() &&
      this.passwordRequirements.hasSpecialChar()
    );
  });

  /**
   * Computed signals for password validation UI
   */
  readonly isNewPasswordInvalid = computed(() => {
    const password = this.newPassword();
    return password.length > 0 && !this.arePasswordRequirementsMet();
  });

  readonly doPasswordsMatch = computed(() => {
    const password = this.newPassword();
    const confirm = this.confirmPassword();
    return password === confirm && password.length > 0;
  });

  readonly isConfirmPasswordInvalid = computed(() => {
    const confirm = this.confirmPassword();
    return confirm.length > 0 && !this.doPasswordsMatch();
  });

  // Personal Preferences
  readonly selectedLanguage = signal(this.activeLanguageCode());
  readonly selectedTimezone = signal('Europe/Istanbul');
  readonly selectedTheme = signal<Appearance>(this.appearanceService.appearance());

  /**
   * Dil değişikliğini yönetir ve hemen uygulama geneline yansıtır.
   * (Kullanıcı "Kaydet" butonuna basmadan önce UI'ın değişmesi için)
   */
  onLanguageChange(lang: string | number): void {
    if (!lang) return;

    const langCode = String(lang).toLowerCase();
    this.selectedLanguage.set(langCode);

    this.translateService.setLanguage(langCode).catch(err => {
      console.error('Failed to change language:', err);
    });

    this.authStateService.setLanguageCookie(langCode);

    // URL'den language parametresini temizle
    this.removeLanguageParamFromUrl();
  }

  /**
   * URL'den language parametresini temizler (hem query string'den hem de hash'ten)
   */
  private removeLanguageParamFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    let hasChanges = false;

    // Query string'den language parametresini temizle
    if (url.searchParams.has('language')) {
      url.searchParams.delete('language');
      hasChanges = true;
    }

    // Hash içindeki query parametrelerini kontrol et ve temizle
    if (url.hash && url.hash.includes('?')) {
      const hashParts = url.hash.split('?');
      if (hashParts.length === 2) {
        const hashParams = new URLSearchParams(hashParts[1]);
        if (hashParams.has('language')) {
          hashParams.delete('language');
          const newHashParams = hashParams.toString();
          url.hash = hashParts[0] + (newHashParams ? `?${newHashParams}` : '');
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      window.history.replaceState({}, '', url.toString());
    }
  }

  // System Preferences
  readonly selectedDateFormat = signal(this.activeDateFormat());
  readonly selectedTimeFormat = signal('24h');
  readonly selectedCurrency = signal('USD');

  // Application Defaults
  readonly selectedToothNotation = signal('FDI');
  readonly selectedFileFormat = signal('STL');
  readonly selectedMouseControl = signal<MouseControlValue>(MOUSE_CONTROL_DEFAULT);
  readonly selectedDefaultApp = signal('');
  readonly isPreferencesSaving = signal(false);

  // Notifications
  readonly emailNotifications = signal(true);
  readonly smsNotifications = signal(false);
  readonly marketingEmails = signal(false);

  // Options for dropdowns - mapped to UserSettingsDto fields
  readonly languageOptions = computed<DropdownOption[]>(() => [
    { value: 'tr', label: 'Türkçe' },
    { value: 'en', label: 'English' },
  ]);

  readonly toothNotationOptions = computed<DropdownOption[]>(() => [
    { value: 'FDI', label: 'FDI' },
    { value: 'Universal', label: 'Universal' },
    { value: 'Palmer', label: 'Palmer' },
  ]);

  readonly isTimezoneMenuOpen = signal(false);
  readonly timezoneQuery = signal('');
  readonly labTimezones: { value: string; city: string; offset: string }[] = [
    { value: 'Europe/Istanbul', city: 'Istanbul', offset: 'UTC+03:00' },
    { value: 'Europe/London', city: 'London', offset: 'UTC+00:00' },
    { value: 'Europe/Berlin', city: 'Berlin', offset: 'UTC+01:00' },
    { value: 'Europe/Paris', city: 'Paris', offset: 'UTC+01:00' },
    { value: 'America/New_York', city: 'New York', offset: 'UTC−05:00' },
    { value: 'America/Los_Angeles', city: 'Los Angeles', offset: 'UTC−08:00' },
    { value: 'Asia/Dubai', city: 'Dubai', offset: 'UTC+04:00' },
    { value: 'Asia/Tokyo', city: 'Tokyo', offset: 'UTC+09:00' },
  ];
  readonly filteredTimezones = computed(() => {
    const query = this.timezoneQuery().trim().toLowerCase();
    if (!query) return this.labTimezones;
    return this.labTimezones.filter((zone) =>
      `${zone.city} ${zone.offset} ${zone.value}`.toLowerCase().includes(query)
    );
  });
  readonly selectedTimezoneLabel = computed(() => {
    const match = this.labTimezones.find((zone) => zone.value === this.selectedTimezone());
    return match ? `${match.city} · ${match.offset}` : this.selectedTimezone();
  });

  private readonly baseLabDateFormats: DropdownOption[] = [
    { value: 'dd.MM.yyyy', label: '08.09.2026' },
    { value: 'dd-MM-yyyy', label: '08-09-2026' },
    { value: 'dd/MM/yyyy', label: '08/09/2026' },
    { value: 'dd MMM yyyy', label: '08 Sep 2026' },
    { value: 'd MMM yyyy', label: '8 Sep 2026' },
    { value: 'MM-dd-yyyy', label: '09-08-2026' },
    { value: 'yyyy-MM-dd', label: '2026-09-08' },
  ];

  readonly dateFormatOptions = computed(() => {
    const active = this.selectedDateFormat();
    if (!active || this.baseLabDateFormats.some((opt) => opt.value === active)) {
      return this.baseLabDateFormats;
    }
    return [{ value: active, label: active }, ...this.baseLabDateFormats];
  });

  readonly timeFormatOptions = computed<DropdownOption[]>(() => [
    { value: '24h', label: this.translateService.getText('SETTINGS.TIME_24_HOUR') },
    { value: '12h', label: this.translateService.getText('SETTINGS.TIME_12_HOUR') },
  ]);

  readonly mouseControlOptions: { value: MouseControlValue; labelKey: string }[] = [
    { value: MOUSE_CONTROL_DEFAULT, labelKey: I18N_SETTINGS_MOUSE_CONTROLS_DEFAULT },
    { value: MOUSE_CONTROL_EXOCAD, labelKey: I18N_SETTINGS_MOUSE_CONTROLS_EXOCAD },
    { value: MOUSE_CONTROL_3SHAPE, labelKey: I18N_SETTINGS_MOUSE_CONTROLS_3SHAPE },
  ];

  private activeToothNotation(raw?: string | null): string {
    const value = (raw ?? '').trim();
    if (!value) return 'FDI';
    const known = ['FDI', 'Universal', 'Palmer'];
    const match = known.find((item) => item.toLowerCase() === value.toLowerCase());
    if (match) return match;
    if (value.toUpperCase() === 'ADA') return 'Universal';
    return 'FDI';
  }

  private activeLanguageCode(): string {
    const lang = (this.translateService.getCurrentLanguage() || 'tr').toLowerCase();
    if (lang.startsWith('tr')) return 'tr';
    if (lang.startsWith('en')) return 'en';
    return lang.slice(0, 2);
  }

  private activeDateFormat(raw?: string | null): string {
    const value = (
      raw ??
      this.authStateService.user()?.userSettingsDto?.dateFormat ??
      'dd.MM.yyyy'
    ).trim();
    return value || 'dd.MM.yyyy';
  }

  private knownDateFormats(): string[] {
    return this.baseLabDateFormats.map((opt) => opt.value);
  }

  private resolveDateFormat(raw?: string | null): string {
    const value = this.activeDateFormat(raw);
    const known = this.knownDateFormats();
    if (known.includes(value)) return value;
    const dashed = value.replace(/\./g, '-');
    if (known.includes(dashed)) return dashed;
    const dotted = value.replace(/-/g, '.');
    if (known.includes(dotted)) return dotted;
    return value;
  }

  toggleTimezoneMenu(): void {
    this.isTimezoneMenuOpen.update((open) => !open);
    if (!this.isTimezoneMenuOpen()) {
      this.timezoneQuery.set('');
    }
  }

  selectTimezone(value: string): void {
    this.selectedTimezone.set(value);
    this.timezoneQuery.set('');
    this.isTimezoneMenuOpen.set(false);
  }

  @HostListener('document:click')
  closeTimezoneMenu(): void {
    this.isTimezoneMenuOpen.set(false);
    this.timezoneQuery.set('');
  }

  onMouseControlChange(value: string | number): void {
    this.selectedMouseControl.set(normalizeMouseControl(value));
  }

  readonly fileFormatOptions = computed<DropdownOption[]>(() => [
    { value: 'STL', label: 'STL' },
    { value: 'OBJ', label: 'OBJ' },
    { value: 'PLY', label: 'PLY' },
  ]);

  ngOnInit(): void {
    this.loadTranslations();
    this.selectedDateFormat.set(this.resolveDateFormat());

    // Load user data from GET User/GetById
    this.loadUserProfile();

    // Load UserSettings
    this.loadUserSettings();

    this.languageChangeSubscription = this.authStateService.languageChange$.subscribe(() => {
      this.selectedLanguage.set(this.activeLanguageCode());
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    this.languageChangeSubscription?.unsubscribe();
  }

  private loadTranslations(): void {
    this.translateService.loadLibrary('account').catch((err) => {
      console.warn('Failed to load account translations:', err);
    });
  }

  /**
   * Load current user from GET User/GetById and fill profile form.
   */
  private loadUserProfile(): void {
    const userId = this.authStateService.user()?.userId;
    if (!userId) {
      const user = this.authStateService.user();
      if (user) {
        this.firstName.set(user.firstName || '');
        this.lastName.set(user.lastName || '');
        this.email.set(user.email || '');
        this.roleDescription.set(user.role || null);
      }
      return;
    }

    this.authApiService.getUser(userId, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.loadedUser = response.data;
          this.loadedAvatarUrl.set(response.data.userAvatarUrl || null);
          this.roleDescription.set(response.data.roleDescription || null);
          this.membershipDate.set(response.data.membershipDate || null);
          this.firstName.set(response.data.firstName ?? '');
          this.lastName.set(response.data.lastName ?? '');
          this.email.set(
            response.data.email ?? this.authStateService.user()?.email ?? ''
          );
        }
      },
      error: (err) => {
        console.error('Failed to load user profile:', err);
        const user = this.authStateService.user();
        if (user) {
          this.firstName.set(user.firstName || '');
          this.lastName.set(user.lastName || '');
          this.email.set(user.email || '');
          this.roleDescription.set(user.role || null);
        }
      },
    });
  }

  isFirstNameInvalid(): boolean {
    return this.firstNameTouched() && !this.firstName().trim();
  }

  isLastNameInvalid(): boolean {
    return this.lastNameTouched() && !this.lastName().trim();
  }

  onSaveProfile(): void {
    this.firstNameTouched.set(true);
    this.lastNameTouched.set(true);
    if (!this.firstName().trim() || !this.lastName().trim()) {
      return;
    }

    const userId = this.authStateService.user()?.userId ?? this.loadedUser?.id;
    if (!userId) {
      this.toastService.error(this.translateService.getText('SETTINGS.USER_NOT_FOUND'));
      return;
    }

    if (!this.loadedUser) {
      this.toastService.error(this.translateService.getText('SETTINGS.USER_NOT_FOUND'));
      return;
    }

    this.isProfileSaving.set(true);

    // Base: full model from GET User/GetById; overlay: form values
    const payload = new UserDto();
    payload.setData({
      ...this.loadedUser,
      id: this.loadedUser.id || userId,
      firstName: this.firstName().trim(),
      lastName: this.lastName().trim(),
      email: this.loadedUser.email,
    });

    this.authApiService.updateUser(userId, payload, true).subscribe({
      next: (response) => {
        this.isProfileSaving.set(false);
        if (response.success && response.data) {
          this.loadedUser = response.data;
          this.authStateService.updateUserProfileInSession({
            firstName: response.data.firstName ?? this.firstName().trim(),
            lastName: response.data.lastName ?? this.lastName().trim(),
          });
          this.toastService.success(this.translateService.getText('SETTINGS.PROFILE_SAVED'));
        } else {
          this.toastService.error(
            response.message ?? this.translateService.getText('SETTINGS.PROFILE_SAVE_FAILED')
          );
        }
      },
      error: (err) => {
        this.isProfileSaving.set(false);
        console.error('Save profile error:', err);
        this.toastService.error(
          err?.error?.message ??
          this.translateService.getText('SETTINGS.PROFILE_SAVE_FAILED') ??
          'An error occurred while saving the profile.'
        );
      },
    });
  }

  onChangePassword(): void {
    if (!this.currentPassword() || !this.newPassword() || !this.confirmPassword()) {
      this.toastService.error(this.translateService.getText('SETTINGS.FILL_ALL_FIELDS'));
      return;
    }

    if (this.newPassword() !== this.confirmPassword()) {
      this.toastService.error(this.translateService.getText('SETTINGS.PASSWORDS_NOT_MATCH'));
      return;
    }

    if (this.newPassword().length < 6) {
      this.toastService.error(this.translateService.getText('SETTINGS.PASSWORD_TOO_SHORT'));
      return;
    }

    const user = this.authStateService.user();
    if (!user?.email) {
      this.toastService.error(this.translateService.getText('SETTINGS.USER_NOT_FOUND'));
      return;
    }

    this.isPasswordChanging.set(true);

    const payload: ChangePasswordRequest = {
      email: user.email,
      phone: '', // Default empty as per requirement
      oldPassword: this.currentPassword(),
      newPassword: this.newPassword(),
    };

    this.userApiService.changePassword(payload, true).subscribe({
      next: (response) => {
        this.isPasswordChanging.set(false);
        if (response.success) {
          this.toastService.success(
            this.translateService.getText('SETTINGS.PASSWORD_CHANGED') || 'Password changed successfully'
          );
          // Reset fields
          this.currentPassword.set('');
          this.newPassword.set('');
          this.confirmPassword.set('');
        } else {
          this.toastService.error(
            response.message ||
            this.translateService.getText('SETTINGS.PASSWORD_CHANGE_FAILED') ||
            'Failed to change password'
          );
        }
      },
      error: (error) => {
        this.isPasswordChanging.set(false);
        console.error('Password change error:', error);
        this.toastService.error(
          this.translateService.getText('SETTINGS.PASSWORD_CHANGE_ERROR') ||
          'An error occurred while changing password'
        );
      },
    });
  }

  onOpenAvatarModal(): void {
    this.isAvatarModalOpen.set(true);
  }

  onCloseAvatarModal(): void {
    this.isAvatarModalOpen.set(false);
  }

  onAvatarSaved(): void {
    this.isAvatarModalOpen.set(false);
    // Toast is already shown by ProfileAvatarDialog
  }

  private loadUserSettings(): void {
    const user = this.authStateService.user();
    if (!user?.userId) {
      return;
    }

    // Fetch UserSettings from API
    this.authApiService.getUserSettings(user.userId, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const settings = new UserSettingsDto();
          settings.setData(response.data);
          this.userSettings = settings;
          this.mapUserSettingsToSignals();
        } else {
          // UserSettings doesn't exist yet, initialize with defaults
          this.userSettings = null;
        }
      },
      error: (error) => {
        console.error('Failed to load user settings:', error);
        // On error, assume UserSettings doesn't exist yet
        this.userSettings = null;
      },
    });
  }

  private mapUserSettingsToSignals(): void {
    if (!this.userSettings) return;

    // Map UserSettings fields to signals
    // dateFormat: 'dd-MM-yyyy' formatına çevir (UserSettings'ta 'dd.MM.yyyy' olabilir)
    this.selectedDateFormat.set(this.resolveDateFormat(this.userSettings.dateFormat));

    // timeFormat
    this.selectedTimeFormat.set(this.userSettings.timeFormat || '24h');

    // currency
    this.selectedCurrency.set(this.userSettings.currency || 'USD');

    this.selectedTheme.set(this.appearanceService.appearance());

    // timeZone
    this.selectedTimezone.set(this.userSettings.timeZone || 'Europe/Istanbul');

    this.selectedToothNotation.set(this.activeToothNotation(this.userSettings.dentalNotation));

    // fileFormat: '.stl' -> 'STL' formatına çevir
    const fileFormat = this.userSettings.fileFormat || '.stl';
    this.selectedFileFormat.set(fileFormat.replace(/^\./, '').toUpperCase());

    this.selectedMouseControl.set(normalizeMouseControl(this.userSettings.mouseControl));

    // language: 'EN' -> 'en', 'TR' -> 'tr'
    this.selectedLanguage.set(this.activeLanguageCode());

    // appType -> defaultApp mapping
    // appType: 0 = none, 1 = clinic, 2 = lab
    const appType = this.userSettings.appType || 0;
    if (appType === 1) {
      this.selectedDefaultApp.set('clinic');
    } else if (appType === 2) {
      this.selectedDefaultApp.set('lab');
    } else {
      this.selectedDefaultApp.set('');
    }

    // Notification preferences
    this.emailNotifications.set(this.userSettings.isEpostaNotification ?? true);
    this.smsNotifications.set(this.userSettings.isSmsNotification ?? false);
    this.marketingEmails.set(this.userSettings.isMarketingEposta ?? false);
  }

  onSavePreferences(): void {
    this.isPreferencesSaving.set(true);

    const user = this.authStateService.user();
    if (!user?.userId) {
      this.toastService.error(this.translateService.getText('SETTINGS.USER_NOT_FOUND'));
      this.isPreferencesSaving.set(false);
      return;
    }

    // Prepare UserSettings payload
    const settingsPayload = new UserSettingsDto();

    const dateFormat = this.selectedDateFormat();

    // fileFormat: 'STL' -> '.stl'
    const fileFormat = '.' + this.selectedFileFormat().toLowerCase();

    // language: 'en' -> 'EN'
    const language = this.selectedLanguage().toUpperCase();

    // defaultApp -> appType mapping
    let appType = 0;
    if (this.selectedDefaultApp() === 'clinic') {
      appType = 1;
    } else if (this.selectedDefaultApp() === 'lab') {
      appType = 2;
    }

    settingsPayload.setData({
      id: this.userSettings?.id || null,
      userId: user.userId,
      dateFormat: dateFormat,
      timeFormat: this.selectedTimeFormat(),
      currency: this.selectedCurrency(),
      theme: this.selectedTheme(),
      timeZone: this.selectedTimezone(),
      dentalNotation: this.selectedToothNotation(),
      fileFormat: fileFormat,
      language: language,
      appType: appType,
      mouseControl: this.selectedMouseControl(),
      isEpostaNotification: this.emailNotifications(),
      isSmsNotification: this.smsNotifications(),
      isMarketingEposta: this.marketingEmails(),
    });

    // Determine if POST or PUT based on id
    const isUpdate = this.userSettings?.id && this.userSettings.id !== null && this.userSettings.id.trim() !== '';
    const apiCall = isUpdate
      ? this.authApiService.updateUserSettings(settingsPayload, true)
      : this.authApiService.createUserSettings(settingsPayload, true);

    apiCall.subscribe({
      next: (response) => {
        this.isPreferencesSaving.set(false);
        if (response.success && response.data) {
          const settings = new UserSettingsDto();
          settings.setData(response.data);
          this.userSettings = settings;
          this.authStateService.updateUserSettingsInSession(settings);
          this.selectedDateFormat.set(this.resolveDateFormat(settings.dateFormat));
          this.toastService.success(this.translateService.getText('SETTINGS.PREFERENCES_SAVED'));
        } else {
          this.toastService.error(response.message || this.translateService.getText('SETTINGS.PREFERENCES_SAVE_FAILED'));
        }
      },
      error: (error) => {
        this.isPreferencesSaving.set(false);
        console.error('Failed to save preferences:', error);
        this.toastService.error(this.translateService.getText('SETTINGS.PREFERENCES_SAVE_FAILED'));
      },
    });
  }
}
